import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Clock, Check, Users, Award, MapPin, Calendar } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { API, useAuth } from "@/lib/auth";
import { SITE } from "@/lib/site";

function formatPrettyDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

function dateLabel(d) {
  const dt = formatPrettyDate(d.date_iso);
  const time = d.start_time && d.end_time ? ` · ${d.start_time}–${d.end_time}` : d.start_time ? ` · ${d.start_time}` : "";
  return `${dt}${time}`;
}

export default function CourseDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [buying, setBuying] = useState(null);
  const [selection, setSelection] = useState(["", ""]);
  const [dates, setDates] = useState([]);
  const [datesLoaded, setDatesLoaded] = useState(false);
  const [selectedDateId, setSelectedDateId] = useState("");
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({ full_name: "", email: "", phone: "", consent: false });
  const [submittingWL, setSubmittingWL] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // { code, kind, label, discount, percent_off }
  const [promoLoading, setPromoLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/courses/${slug}`)
      .then((r) => setCourse(r.data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); });
    axios.get(`${API}/courses/${slug}/dates`).then((r) => {
      setDates(r.data || []);
      setDatesLoaded(true);
    }).catch(() => setDatesLoaded(true));
  }, [slug]);

  useEffect(() => {
    if (user && !waitlistForm.email) {
      setWaitlistForm((f) => ({
        ...f,
        email: user.email || "",
        full_name: user.name || "",
      }));
    }
  }, [user]);   // eslint-disable-line

  const selectedDate = useMemo(
    () => dates.find((d) => d.id === selectedDateId) || null,
    [dates, selectedDateId],
  );
  const selectedIsSoldOut = !!(selectedDate && selectedDate.sold_out);
  const hasDates = dates.length > 0;

  const buy = async (lookupKey) => {
    if (course?.selection) {
      const chosen = selection.filter(Boolean);
      if (chosen.length < course.selection.min) {
        toast.error(`Please select ${course.selection.min} methods to continue`);
        return;
      }
      if (new Set(chosen).size !== chosen.length) {
        toast.error("Please choose two different methods");
        return;
      }
    }
    if (hasDates) {
      if (!selectedDateId) {
        toast.error("Please pick a training date to continue");
        return;
      }
      if (selectedIsSoldOut) {
        toast.error("This date is sold out — please join the waitlist instead");
        return;
      }
    }
    setBuying(lookupKey);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        quantity: 1,
        origin_url: window.location.origin,
        selections: course?.selection ? selection.filter(Boolean) : undefined,
        course_date_id: hasDates ? selectedDateId : undefined,
        promo_code: promo ? promo.code : undefined,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBuying(null);
    }
  };

  const applyCoursePromo = async (lookup_key, amount) => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const r = await axios.post(`${API}/checkout/validate-code`, {
        code: promoInput.trim().toUpperCase(),
        subtotal: Number(amount) || 0,
        context: "academy",
      });
      if (r.data?.valid) {
        setPromo({
          code: promoInput.trim().toUpperCase(),
          kind: r.data.kind,
          label: r.data.label,
          discount: r.data.discount,
        });
        toast.success(`${r.data.label} applied`);
      } else {
        setPromo(null);
        toast.error(r.data?.message || "Invalid or expired code");
      }
    } catch {
      setPromo(null);
      toast.error("Invalid or expired code");
    } finally {
      setPromoLoading(false);
    }
  };

  const submitWaitlist = async (e) => {
    e.preventDefault();
    const { full_name, email, phone, consent } = waitlistForm;
    if (!full_name || full_name.trim().length < 2) return toast.error("Please enter your full name");
    if (!email) return toast.error("Please enter your email");
    if (!phone || phone.trim().length < 6) return toast.error("Please enter your phone number");
    if (!consent) return toast.error("Please tick the consent box");
    setSubmittingWL(true);
    try {
      await axios.post(`${API}/waitlist`, {
        course_slug: slug,
        full_name: full_name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        preferred_date: selectedDate?.date_iso || null,
        consent,
      });
      toast.success("You're on the waitlist — we'll email you when a new date opens.");
      setShowWaitlist(false);
      setWaitlistForm({ full_name: "", email: "", phone: "", consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join waitlist. Please try again.");
    } finally {
      setSubmittingWL(false);
    }
  };

  if (notFound) {
    return (
      <div data-testid="page-course-not-found" className="pt-40 pb-24 text-center">
        <div className="overline">404</div>
        <h1 className="font-display text-5xl mt-3 tracking-tight">Course not found.</h1>
        <p className="mt-4 text-[hsl(var(--onyx)/0.6)]">The course you're looking for doesn't exist or was moved.</p>
        <Link to="/academy" className="btn-pill btn-primary mt-8 inline-flex">← Back to all courses</Link>
      </div>
    );
  }

  if (!course) {
    return <div className="pt-40 pb-24 text-center text-[hsl(var(--onyx)/0.5)]">Loading…</div>;
  }

  const bookingBlocked = hasDates && (!selectedDateId || selectedIsSoldOut);

  return (
    <div data-testid={`page-course-${slug}`} className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className={`rounded-[2.25rem] overflow-hidden aspect-[4/3] ${course.poster_image ? "bg-blush" : "hover-zoom"}`}>
              <img
                src={course.image}
                alt={course.name}
                className={`w-full h-full ${course.poster_image ? "object-contain" : "object-cover"}`}
              />
            </div>

            <div className="mt-10">
              <div className="overline">NøoksPro Training Academy</div>
              <h1 className="font-display text-5xl sm:text-6xl mt-3 tracking-tight text-balance">
                {course.name}
              </h1>
              <p className="mt-5 text-lg text-[hsl(var(--onyx)/0.7)] leading-relaxed">
                {course.tagline}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                {[
                  { i: Clock, v: course.duration, show: true },
                  { i: Users, v: course.who_for, show: true },
                  { i: Award, v: "Accredited", show: !!course.accredited },
                ].filter((x) => x.show).map(({ i: Icon, v }, k) => (
                  <div
                    key={k}
                    className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--onyx)/0.1)] px-4 py-2 text-sm"
                  >
                    <Icon size={14} className="text-[hsl(var(--gold))]" />
                    {v}
                  </div>
                ))}
              </div>
            </div>

            {course.about && course.about.length > 0 && (
              <Reveal className="mt-14">
                <div className="overline">About the programme</div>
                <div className="mt-6 space-y-5 text-[hsl(var(--onyx)/0.75)] leading-relaxed max-w-2xl">
                  {course.about.map((para, i) => (
                    <p key={i} className={i === 0 ? "text-lg" : "text-base"}>{para}</p>
                  ))}
                </div>
              </Reveal>
            )}

            <Reveal className="mt-14">
              <div className="overline">What you'll learn</div>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                {course.what_you_learn.map((w, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blush flex items-center justify-center shrink-0">
                      <Check size={14} />
                    </div>
                    <span className="text-[hsl(var(--onyx)/0.75)]">{w}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="mt-14">
              <div className="overline">What's included</div>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                {course.whats_included.map((w, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] flex items-center justify-center shrink-0">
                      <Check size={14} />
                    </div>
                    <span className="text-[hsl(var(--onyx)/0.75)]">{w}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-28 rounded-[2rem] bg-white border border-[hsl(var(--onyx)/0.08)] p-8 shadow-[0_20px_60px_-30px_rgba(20,10,15,0.2)]">
              <div className="overline">From</div>
              <div className="font-display text-5xl mt-1 text-[hsl(var(--onyx))]">
                {course.price_from}
              </div>
              <p className="mt-3 text-sm text-[hsl(var(--onyx)/0.55)]">
                Certification · {course.certification}
              </p>

              {course.selection && (
                <div className="mt-8 space-y-3" data-testid="course-selection">
                  <div className="overline">{course.selection.label}</div>
                  {course.selection.helper && (
                    <p className="text-xs text-[hsl(var(--onyx)/0.55)]">{course.selection.helper}</p>
                  )}
                  {Array.from({ length: course.selection.max }).map((_, i) => {
                    const other = selection.filter((_, j) => j !== i);
                    return (
                      <div key={i}>
                        <label className="text-xs text-[hsl(var(--onyx)/0.55)]">
                          {i === 0 ? "First method" : `Method ${i + 1}`}
                        </label>
                        <select
                          data-testid={`course-selection-${i + 1}`}
                          value={selection[i] || ""}
                          onChange={(e) => {
                            const next = [...selection];
                            next[i] = e.target.value;
                            setSelection(next);
                          }}
                          className="w-full mt-1 h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))] bg-white"
                        >
                          <option value="">Choose a method…</option>
                          {course.selection.options.map((opt) => (
                            <option key={opt} value={opt} disabled={other.includes(opt)}>
                              {opt}{other.includes(opt) ? " (already chosen)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Course date selector — only rendered if the course has upcoming dates */}
              {datesLoaded && hasDates && (
                <div className="mt-8 space-y-3" data-testid="course-date-picker">
                  <div className="overline flex items-center gap-2">
                    <Calendar size={12} /> Pick your training date
                  </div>
                  <select
                    data-testid="course-date-select"
                    value={selectedDateId}
                    onChange={(e) => {
                      setSelectedDateId(e.target.value);
                      setShowWaitlist(false);
                    }}
                    className="w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))] bg-white"
                  >
                    <option value="">-- Choose a date --</option>
                    {dates.map((d) => (
                      <option
                        key={d.id}
                        value={d.id}
                        disabled={d.sold_out}
                        style={d.sold_out ? { color: "#999" } : undefined}
                      >
                        {dateLabel(d)}{d.sold_out ? " — SOLD OUT" : ` · ${d.spaces_remaining} left`}
                      </option>
                    ))}
                  </select>

                  {selectedDate && (
                    <div
                      data-testid="course-date-details"
                      className={`rounded-2xl border p-4 text-sm space-y-2 ${
                        selectedIsSoldOut
                          ? "bg-[hsl(var(--onyx)/0.04)] border-[hsl(var(--onyx)/0.12)] text-[hsl(var(--onyx)/0.55)]"
                          : "bg-blush/40 border-[hsl(var(--onyx)/0.08)]"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-medium text-[hsl(var(--onyx))]">
                          {formatPrettyDate(selectedDate.date_iso)}
                        </div>
                        {selectedIsSoldOut ? (
                          <span
                            data-testid="course-date-soldout-badge"
                            className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                          >
                            Sold Out
                          </span>
                        ) : (
                          <span className="text-xs text-[hsl(var(--onyx)/0.7)]">
                            {selectedDate.spaces_remaining} space{selectedDate.spaces_remaining === 1 ? "" : "s"} left
                          </span>
                        )}
                      </div>
                      {(selectedDate.start_time || selectedDate.end_time) && (
                        <div className="flex items-center gap-2 text-xs text-[hsl(var(--onyx)/0.65)]">
                          <Clock size={12} />
                          {selectedDate.start_time}{selectedDate.end_time ? ` – ${selectedDate.end_time}` : ""}
                        </div>
                      )}
                      {selectedDate.location && (
                        <div className="flex items-center gap-2 text-xs text-[hsl(var(--onyx)/0.65)]">
                          <MapPin size={12} />
                          {selectedDate.location}
                        </div>
                      )}
                      {selectedDate.notes && !selectedIsSoldOut && (
                        <div className="text-xs text-[hsl(var(--onyx)/0.6)] pt-1">{selectedDate.notes}</div>
                      )}
                    </div>
                  )}

                  {selectedIsSoldOut && !showWaitlist && (
                    <button
                      type="button"
                      onClick={() => setShowWaitlist(true)}
                      data-testid="course-waitlist-open"
                      className="btn-pill btn-outline w-full"
                    >
                      Join the waitlist →
                    </button>
                  )}
                </div>
              )}

              {/* Waitlist form (shown when a sold-out date is selected & user opens it) */}
              {showWaitlist && (
                <form
                  onSubmit={submitWaitlist}
                  data-testid="course-waitlist-form"
                  className="mt-6 space-y-3 p-5 rounded-2xl bg-white border border-[hsl(var(--onyx)/0.1)]"
                >
                  <div className="overline">Join the waitlist</div>
                  <p className="text-xs text-[hsl(var(--onyx)/0.6)] leading-relaxed">
                    We'll email you the moment a new date opens for {course.name}.
                  </p>
                  <input
                    type="text"
                    placeholder="Full name"
                    data-testid="wl-name"
                    value={waitlistForm.full_name}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, full_name: e.target.value })}
                    className="w-full h-11 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    data-testid="wl-email"
                    value={waitlistForm.email}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })}
                    className="w-full h-11 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    data-testid="wl-phone"
                    value={waitlistForm.phone}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, phone: e.target.value })}
                    className="w-full h-11 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                  <label className="flex items-start gap-2 text-xs text-[hsl(var(--onyx)/0.7)] leading-relaxed cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="wl-consent"
                      checked={waitlistForm.consent}
                      onChange={(e) => setWaitlistForm({ ...waitlistForm, consent: e.target.checked })}
                      className="mt-0.5"
                    />
                    <span>I'd like to be emailed when new dates for this course become available.</span>
                  </label>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowWaitlist(false)}
                      className="btn-pill btn-outline flex-1 !text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      data-testid="wl-submit"
                      disabled={submittingWL}
                      className="btn-pill btn-primary flex-1 !text-xs disabled:opacity-60"
                    >
                      {submittingWL ? "Joining…" : "Join waitlist"}
                    </button>
                  </div>
                </form>
              )}

              <div className="mt-6 flex flex-col gap-3">
                {/* Promo code — same 34% off codes as shop, e.g. BIRTHDAY34 */}
                <div className="rounded-2xl bg-white/80 backdrop-blur border border-[hsl(var(--onyx)/0.08)] p-3">
                  <div className="flex items-center gap-2">
                    <input
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      placeholder="Got a code?"
                      disabled={!!promo}
                      data-testid="course-promo-input"
                      className="flex-1 h-10 rounded-full bg-white border border-[hsl(var(--onyx)/0.12)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))] disabled:opacity-60"
                    />
                    {promo ? (
                      <button
                        type="button"
                        onClick={() => { setPromo(null); setPromoInput(""); }}
                        data-testid="course-promo-clear"
                        className="text-xs text-[hsl(var(--onyx)/0.6)] hover:text-[hsl(var(--burgundy))] px-3"
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          const first = course.lookup_keys[0];
                          applyCoursePromo(first?.lookup_key, first?.amount || 0);
                        }}
                        disabled={promoLoading || !promoInput.trim()}
                        data-testid="course-promo-apply"
                        className="btn-pill btn-outline !text-xs disabled:opacity-50"
                      >
                        {promoLoading ? "Checking…" : "Apply"}
                      </button>
                    )}
                  </div>
                  {promo && (
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className="text-[hsl(var(--gold))] font-medium" data-testid="course-promo-label">
                        {promo.label || promo.code}
                      </span>
                      <span className="text-[hsl(var(--onyx)/0.6)]">
                        −£{Number(promo.discount || 0).toFixed(2)} on first option
                      </span>
                    </div>
                  )}
                </div>
                {course.lookup_keys.map((lk) => {
                  const price = Number(lk.amount) || 0;
                  const showDiscounted =
                    promo && promo.kind !== "voucher" && price > 0;
                  const discountedPrice = showDiscounted
                    ? Math.max(0, price - (promo.kind === "bundle"
                        ? (price * (Number(promo.percent_off) || 0)) / 100
                        : (promo.label && promo.label.includes("%")
                            ? price * (parseFloat(promo.label) / 100)
                            : Math.min(price, Number(promo.discount) || 0))))
                    : price;
                  return (
                    <button
                      key={lk.lookup_key}
                      onClick={() => buy(lk.lookup_key)}
                      disabled={buying === lk.lookup_key || bookingBlocked}
                      data-testid={`course-buy-${lk.lookup_key}`}
                      className="btn-pill btn-primary justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                      title={bookingBlocked ? (selectedIsSoldOut ? "This date is sold out" : "Please pick a training date") : undefined}
                    >
                      <span>{lk.label}</span>
                      <span className="flex items-center gap-1">
                        {showDiscounted && discountedPrice < price ? (
                          <>
                            <span className="line-through opacity-60 text-xs">£{price}</span>
                            £{discountedPrice.toFixed(2)}
                          </>
                        ) : (
                          <>£{lk.amount}</>
                        )}
                        <ArrowUpRight size={14} />
                      </span>
                    </button>
                  );
                })}
                {hasDates && !selectedDateId && (
                  <p className="text-xs text-[hsl(var(--onyx)/0.55)] text-center">
                    Pick a training date above to enable checkout.
                  </p>
                )}
                <a
                  href={SITE.bookingUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="course-enquire"
                  className="btn-pill btn-outline mt-1"
                >
                  Enquire first
                </a>

                <div className="mt-4 text-xs text-[hsl(var(--onyx)/0.6)] text-center leading-relaxed">
                  Pay in 3 with <span className="font-medium text-[hsl(var(--onyx))]">Klarna</span> or 4 with <span className="font-medium text-[hsl(var(--onyx))]">Clearpay</span> — interest-free.
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-[hsl(var(--onyx)/0.08)] text-xs text-[hsl(var(--onyx)/0.55)] space-y-2">
                <div>✓ Payment plans available</div>
                <div>✓ Secure checkout by Stripe</div>
                <div>✓ Access to your course in your account</div>
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-16">
          <Link
            to="/academy"
            className="text-sm text-[hsl(var(--onyx)/0.6)] hover:text-[hsl(var(--onyx))]"
          >
            ← Back to all courses
          </Link>
        </div>
      </div>
    </div>
  );
}
